import { Badge, Card, CoursePreviewSkeleton, SidebarTrigger, StatusBadge, Heading, Container } from "@athena/ui";
import React, { useEffect, useRef, useState } from "react";
import {
    Check,
    ChevronLeft,
    Plus,
    Eye,
    EyeClosed,
    Pencil,
    ListSortDescending,
    Loader2,
    GripVertical,
} from "lucide-react";
import { Button, Mono, Switch, Text } from "@athena/ui";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ModuleCard } from "./ModuleCard";
import CourseOutline from "./CourseOutline";
import ContentRow from "./ContentRow";
import { ContentEditor } from "./ContentEditor";
import { LearnerPreview } from "./LearnerPreview";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { CombinedGraphQLErrors } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { toast } from "sonner";
import {
    CourseByIdDocument,
    CreateCourseDocument,
    CreateModuleDocument,
    UpdateCourseDocument,
    UpdateModuleDocument,
    DeleteModuleDocument,
    AddTagToCourseDocument,
    RemoveTagFromCourseDocument,
    AddPresenterToCourseDocument,
    RemovePresenterFromCourseDocument,
    CreateVideoContentDocument,
    CreateTextContentDocument,
    CreateQuizContentDocument,
    UpdateVideoContentDocument,
    UpdateTextContentDocument,
    UpdateQuizContentDocument,
    DeleteVideoContentDocument,
    DeleteTextContentDocument,
    DeleteQuizContentDocument,
    type CourseByIdQuery,
} from "../../graphql/courses.generated";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { TagsDocument } from "../../graphql/tags.generated";
import { PresentersDocument } from "../../graphql/presenters.generated";
import type { ContentType } from "@/types/CourseTypes";
import { ErrorFallback } from "@/components/ErrorFallback";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { CourseDetailsBuilder } from "./CourseDetailsTable";
import { CoursePresentersTable } from "./CoursePresentersTable";
import { CourseTagsTable } from "./CourseTagsTable";

type ActiveSection = "details" | string; // string = content item id
type Course = NonNullable<CourseByIdQuery["course"]>;
export type ContentItem = Course["content"][number];
// The query selects content flat (see courses.graphql) and grouped client-side below —
// `content` here isn't part of the raw query type, so it's added on. Exported so sibling
// components (ContentEditor, CourseOutline, LearnerPreview) share this augmented shape
// instead of each re-deriving their own from the raw query type.
export type Module = Course["modules"][number] & { content: ContentItem[] };

const SortableModuleWrapper = ({
    module,
    children,
}: {
    module: Module;
    children: (props: ReturnType<typeof useSortable>) => React.ReactNode;
}) => {
    const sortable = useSortable({ id: module.id });
    return (
        <div style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}>
            {children(sortable)}
        </div>
    );
};

const CoursePreview: React.FC = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const isNew = !id;

    const [createCourse] = useMutation(CreateCourseDocument);
    const [updateCourse] = useMutation(UpdateCourseDocument);

    const [createModule] = useMutation(CreateModuleDocument);
    const [updateModule] = useMutation(UpdateModuleDocument);
    const [deleteModule] = useMutation(DeleteModuleDocument);

    const [addTag] = useMutation(AddTagToCourseDocument);
    const [removeTag] = useMutation(RemoveTagFromCourseDocument);

    const [addPresenter] = useMutation(AddPresenterToCourseDocument);
    const [removePresenter] = useMutation(RemovePresenterFromCourseDocument);

    const [createVideo] = useMutation(CreateVideoContentDocument);
    const [updateVideo] = useMutation(UpdateVideoContentDocument);
    const [deleteVideo] = useMutation(DeleteVideoContentDocument);

    const [createText] = useMutation(CreateTextContentDocument);
    const [updateText] = useMutation(UpdateTextContentDocument);
    const [deleteText] = useMutation(DeleteTextContentDocument);

    const [createQuiz] = useMutation(CreateQuizContentDocument);
    const [updateQuiz] = useMutation(UpdateQuizContentDocument);
    const [deleteQuiz] = useMutation(DeleteQuizContentDocument);

    const creatingRef = useRef(false);
    const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [
        isSaving,
        setIsSaving,
    ] = useState<boolean>(false);
    const [
        savedRecently,
        setSavedRecently,
    ] = useState<boolean>(false);
    const savedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    async function save<T>(fn: () => Promise<T>): Promise<T> {
        setIsSaving(true);
        setSavedRecently(false);
        try {
            const result = await fn();
            if (savedTimeout.current) clearTimeout(savedTimeout.current);
            setSavedRecently(true);
            savedTimeout.current = setTimeout(() => setSavedRecently(false), 2500);
            return result;
        } finally {
            setIsSaving(false);
        }
    }

    useEffect(() => {
        if (!isNew || creatingRef.current) return;
        creatingRef.current = true;
        createCourse({
            variables: {
                // organizationId is never sent — the server derives it entirely (the caller's
                // own org, or the shared-courses sentinel org for a global admin with none).
                // It's reassignable afterward from the org picker on this page (admins only).
                input: { title: "Untitled course", status: "DRAFT" },
            },
        }).then((res) => {
            const newId = res.data?.createCourse.id;
            if (newId) navigate(`/course-preview/${newId}`, { replace: true });
        });
    }, []);

    const { data: meData } = useQuery(CurrentUserDocument);
    const isGlobalAdmin = meData?.currentUser?.role === "ADMIN" || meData?.currentUser?.role === "SYSTEM";
    // Only a global admin can move a course to another org (or the shared-courses sentinel
    // org) — see updateCourse's organizationId gate — so nobody else is offered the picker.
    const changeOrganization = (organizationId: string) => {
        if (!id) return;
        save(() => updateCourse({ variables: { id, input: { organizationId } } }));
    };

    const {
        data: courseData,
        loading: courseLoading,
        error: courseError,
    } = useQuery(CourseByIdDocument, {
        variables: { id: id ?? state?.course?.id ?? "" },
        skip: isNew || (!id && !state?.course?.id),
    });
    const { data: tagsData, loading: tagsLoading, error: tagsError } = useQuery(TagsDocument);
    const { data: presentersData, loading: presentersLoading, error: presentersError } = useQuery(PresentersDocument);

    const course = courseData?.course;
    const tags = tagsData?.tags ?? [];
    const presenters = presentersData?.presenters ?? [];
    const currentUser = meData?.currentUser;
    const author =
        currentUser?.firstName && currentUser?.lastName
            ? `${currentUser.firstName} ${currentUser.lastName}`
            : currentUser?.email;

    const scheduleSave = (newTitle: string | undefined, newDesc: string | undefined) => {
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(() => {
            if (!id) return;
            save(() => updateCourse({ variables: { id, input: { title: newTitle, description: newDesc } } }));
        }, 800);
    };

    const changeAssignedOnly = (assignedOnly: boolean) => {
        if (!id) return;
        save(() => updateCourse({ variables: { id, input: { assignedOnly } } }));
    };

    const handleTogglePublish = () => {
        if (!id || !course) return;
        const status = course.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
        save(() => updateCourse({ variables: { id, input: { status } } })).catch((error: unknown) => {
            // The server refuses a course whose quizzes cannot be passed, and says which questions
            // to fix — that message is for the author, so it is shown as it comes.
            const refusal = CombinedGraphQLErrors.is(error)
                ? error.errors.find((e) => e.extensions?.code === "BAD_USER_INPUT")
                : undefined;
            toast.error(refusal?.message ?? t("admin.lms.publishFailed"));
        });
    };

    const derivedModules: Module[] = React.useMemo(() => {
        if (!course) return [];
        const contentByModule = new Map<string, ContentItem[]>();
        for (const item of course.content) {
            if (!item.moduleId) continue;
            const list = contentByModule.get(item.moduleId);
            if (list) list.push(item);
            else contentByModule.set(item.moduleId, [item]);
        }
        return [...course.modules]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((m) => ({
                ...m,
                content: (contentByModule.get(m.id) ?? []).sort((a, b) => a.sortOrder - b.sortOrder),
            }));
    }, [course]);

    const [
        title,
        setTitle,
    ] = useState<string | undefined>(course?.title ?? "");
    const [
        description,
        setDescription,
    ] = useState<string | undefined>(course?.description ?? "");

    const [
        selectedTags,
        setSelectedTags,
    ] = useState<Set<string>>(new Set(course?.tags?.map((t) => t.id) ?? []));
    const [
        selectedPresenters,
        setSelectedPresenters,
    ] = useState<Set<string>>(new Set(course?.presenters?.map((p) => p.id) ?? []));
    const [
        tagsOpen,
        setTagsOpen,
    ] = useState<boolean>(false);
    const [
        sidebarCollapsed,
        setSidebarCollapsed,
    ] = useState<boolean>(true);
    const [
        modules,
        setModules,
    ] = useState<Module[]>([]);
    const [
        activeSection,
        setActiveSection,
    ] = useState<ActiveSection>("details");
    const [
        editingContentId,
        setEditingContentId,
    ] = useState<string | null>(null);
    const [
        addingContentToModule,
        setAddingContentToModule,
    ] = useState<string | null>(null);
    const [
        isPreviewMode,
        setIsPreviewMode,
    ] = useState<boolean>(false);

    useEffect(() => {
        setModules(derivedModules);
    }, [derivedModules]);

    useEffect(() => {
        if (!course) return;
        setTitle(course.title);
        setDescription(course.description ?? "");
        setSelectedTags(new Set(course.tags.map((t) => t.id)));
        setSelectedPresenters(new Set(course.presenters.map((p) => p.id)));
    }, [course]);

    const highlightSection = (id: string) => {
        setActiveSection(id);
        setTimeout(() => setActiveSection("details"), 1500);
    };
    // Content with no moduleId — never assigned to a module, so it has nowhere to render
    // inside the modules list below. Without surfacing it separately, it stays invisible
    // here even though it counts toward the course's item total everywhere else (e.g. the
    // Courses list card) — see the `content` field's comment in courses.graphql.
    const unmoduledContent = React.useMemo(
        () => (course?.content ?? []).filter((c) => !c.moduleId).sort((a, b) => a.sortOrder - b.sortOrder),
        [course],
    );
    const allContent = [
        ...modules.flatMap((m) => m.content.map((c) => ({ ...c, moduleName: m.title }))),
        ...unmoduledContent.map((c) => ({ ...c, moduleName: "" })),
    ];

    const addModule = async (): Promise<void> => {
        if (!id) return;
        const tempId = `module-new-${Date.now()}`;
        const sortOrder = modules.length;
        setModules((prev) => [
            ...prev,
            {
                __typename: "Module" as const,
                id: tempId,
                courseId: id,
                title: "New module",
                sortOrder,
                content: [],
            },
        ]);
        const res = await save(() =>
            createModule({
                variables: {
                    input: {
                        courseId: id,
                        title: "New module",
                        sortOrder,
                    },
                },
            }),
        );
        const realId = res.data?.createModule.id;
        if (realId) {
            setModules((prev) => prev.map((m) => (m.id === tempId ? { ...m, id: realId } : m)));
        }
    };

    const deleteModuleItem = async (moduleId: string): Promise<void> => {
        const targetModule = modules.find((m) => m.id === moduleId);
        if (!targetModule || targetModule.content.length > 0) return;

        await save(() => deleteModule({ variables: { id: moduleId } }));
        setModules((prev) => prev.filter((m) => m.id !== moduleId));
    };

    const addContentToModule = async (moduleId: string, contentType: ContentType): Promise<void> => {
        const tempId = `content-new-${Date.now()}`;
        const sortOrder = modules.find((m) => m.id === moduleId)?.content.length ?? 0;

        const optimisticItem: ContentItem =
            contentType === "VIDEO"
                ? {
                      __typename: "VideoContent",
                      id: tempId,
                      contentType: "VIDEO",
                      title: "New video",
                      description: null,
                      sortOrder,
                      globalSortOrder: sortOrder,
                      moduleId,
                      status: "SHOWN",
                      required: false,
                      brands: [],
                      tags: [],
                      videoUrl: "",
                      thumbnailUrl: null,
                  }
                : contentType === "TEXT"
                  ? {
                        __typename: "TextContent",
                        id: tempId,
                        contentType: "TEXT",
                        title: "New reading",
                        description: null,
                        sortOrder,
                        globalSortOrder: sortOrder,
                        moduleId,
                        status: "SHOWN",
                        required: false,
                        brands: [],
                        tags: [],
                        body: "",
                    }
                  : {
                        __typename: "QuizContent",
                        id: tempId,
                        contentType: "QUIZ",
                        title: "New quiz",
                        description: null,
                        sortOrder,
                        globalSortOrder: sortOrder,
                        moduleId,
                        status: "SHOWN",
                        required: false,
                        brands: [],
                        tags: [],
                        passThreshold: 80,
                        questions: [],
                    };

        setModules((prev) =>
            prev.map((m) =>
                m.id === moduleId
                    ? {
                          ...m,
                          content: [
                              ...m.content,
                              optimisticItem,
                          ],
                      }
                    : m,
            ),
        );
        setAddingContentToModule(null);

        if (!id) return;
        let realId: string | undefined;
        if (contentType === "VIDEO") {
            const res = await save(() =>
                createVideo({
                    variables: { input: { courseId: id, moduleId, title: "New video", videoUrl: "", sortOrder } },
                }),
            );
            realId = res.data?.createVideoContent.id;
        } else if (contentType === "TEXT") {
            const res = await save(() =>
                createText({
                    variables: { input: { courseId: id, moduleId, title: "New reading", body: "", sortOrder } },
                }),
            );
            realId = res.data?.createTextContent.id;
        } else {
            const res = await save(() =>
                createQuiz({ variables: { input: { courseId: id, moduleId, title: "New quiz", sortOrder } } }),
            );
            realId = res.data?.createQuiz.id;
        }

        if (realId) {
            setModules((prev) =>
                prev.map((m) =>
                    m.id === moduleId
                        ? { ...m, content: m.content.map((c) => (c.id === tempId ? { ...c, id: realId ?? "" } : c)) }
                        : m,
                ),
            );
            setEditingContentId(realId);
        }
    };

    const toggleContentStatus = (contentId: string) => {
        const contentItem = modules.flatMap((m) => m.content).find((c) => c.id === contentId);
        if (!contentItem) return;
        const status = contentItem.status === "SHOWN" ? "HIDDEN" : "SHOWN";

        setModules((prev) =>
            prev.map((m) => ({
                ...m,
                content: m.content.map((c) => (c.id === contentId ? { ...c, status } : c)),
            })),
        );

        if (contentItem.__typename === "VideoContent") {
            save(() => updateVideo({ variables: { id: contentId, input: { status } } }));
        } else if (contentItem.__typename === "TextContent") {
            save(() => updateText({ variables: { id: contentId, input: { status } } }));
        } else {
            save(() => updateQuiz({ variables: { id: contentId, input: { status } } }));
        }
    };

    const deleteContentItem = async (contentId: string): Promise<void> => {
        const content = modules.flatMap((m) => m.content).find((c) => c.id === contentId);
        if (!content) return;

        if (content.__typename === "VideoContent") {
            await save(() => deleteVideo({ variables: { id: contentId } }));
        } else if (content.__typename === "TextContent") {
            await save(() => deleteText({ variables: { id: contentId } }));
        } else {
            await save(() => deleteQuiz({ variables: { id: contentId } }));
        }

        setModules((prev) => prev.map((m) => ({ ...m, content: m.content.filter((c) => c.id !== contentId) })));
        if (editingContentId === contentId) setEditingContentId(null);
    };

    const makeToggleSelection =
        (
            selected: Set<string>,
            setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
            add: (itemId: string) => void,
            remove: (itemId: string) => void,
        ) =>
        (itemId: string) => {
            const isSelected = selected.has(itemId);
            setSelected((prev) => {
                const next = new Set(prev);
                isSelected ? next.delete(itemId) : next.add(itemId);
                return next;
            });
            if (!id) return;
            isSelected ? remove(itemId) : add(itemId);
        };

    const togglePresenter = makeToggleSelection(
        selectedPresenters,
        setSelectedPresenters,
        (presenterId) => addPresenter({ variables: { input: { courseId: id ?? "", presenterId } } }),
        (presenterId) => removePresenter({ variables: { input: { courseId: id ?? "", presenterId } } }),
    );

    const toggleTag = makeToggleSelection(
        selectedTags,
        setSelectedTags,
        (tagId) => addTag({ variables: { input: { courseId: id ?? "", tagId } } }),
        (tagId) => removeTag({ variables: { input: { courseId: id ?? "", tagId } } }),
    );

    const handlePreviewClick = () => setIsPreviewMode((v) => !v);

    const [
        activeModuleId,
        setActiveModuleId,
    ] = useState<string | null>(null);

    const moduleSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleModuleDragStart = (event: DragStartEvent) => {
        setActiveModuleId(String(event.active.id));
    };

    const handleModuleDragEnd = (event: DragEndEvent) => {
        setActiveModuleId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fromIndex = modules.findIndex((m) => m.id === active.id);
        const toIndex = modules.findIndex((m) => m.id === over.id);
        if (fromIndex === -1 || toIndex === -1) return;
        const reordered = arrayMove(modules, fromIndex, toIndex);
        setModules(reordered);
        const changed = reordered.filter((m, idx) => modules[idx]?.id !== m.id);
        if (changed.length > 0) {
            save(() =>
                Promise.all(
                    changed.map((m) => {
                        const idx = reordered.indexOf(m);
                        return updateModule({ variables: { id: m.id, input: { sortOrder: idx } } });
                    }),
                ),
            );
        }
    };

    const handleContentReorder = (moduleId: string, fromIndex: number, toIndex: number) => {
        setModules((prev) =>
            prev.map((m) => {
                if (m.id !== moduleId) return m;
                const reordered = arrayMove(m.content, fromIndex, toIndex);
                const changed = reordered.filter((c, idx) => m.content[idx]?.id !== c.id);
                if (changed.length > 0) {
                    save(() =>
                        Promise.all(
                            changed.map((c) => {
                                const idx = reordered.indexOf(c);
                                if (c.__typename === "VideoContent") {
                                    return updateVideo({ variables: { id: c.id, input: { sortOrder: idx } } });
                                } else if (c.__typename === "TextContent") {
                                    return updateText({ variables: { id: c.id, input: { sortOrder: idx } } });
                                } else {
                                    return updateQuiz({ variables: { id: c.id, input: { sortOrder: idx } } });
                                }
                            }),
                        ),
                    );
                }
                return { ...m, content: reordered };
            }),
        );
    };

    if (courseLoading || isNew) return <CoursePreviewSkeleton />;

    if (!course) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Text muted>{t("admin.lms.courseNotFound")}</Text>
            </div>
        );
    }

    if (courseError) {
        return (
            <Container maxWidth="full" className="p-6">
                <ErrorFallback error={courseError} />
            </Container>
        );
    }

    return (
        <div>
            <div className="flex h-screen flex-col overflow-hidden">
                <div className="border-border bg-background flex h-12 shrink-0 items-center justify-between border-b px-4">
                    <div className="flex items-center gap-3">
                        <SidebarTrigger />
                        <div className="bg-border h-4 w-px" />
                        <button
                            className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm transition-colors"
                            onClick={() => navigate("/courses")}
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                            {t("nav.courses")}
                        </button>
                        <div className="bg-border h-4 w-px" />
                        <div className="flex items-center gap-2">
                            <Text weight="semibold" size="sm">
                                {title || course.title}
                            </Text>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {(isSaving || savedRecently) && (
                            <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
                                {isSaving ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Check className="text-success h-3.5 w-3.5" />
                                )}
                                {isSaving ? "Saving…" : "Saved"}
                            </span>
                        )}
                        <div className="bg-border h-4 w-px" />
                        {!isPreviewMode && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1.5"
                                onClick={() => setSidebarCollapsed((c) => !c)}
                            >
                                <ListSortDescending className="h-3.5 w-3.5" />
                                {t("admin.lms.outline")}
                            </Button>
                        )}
                        <Button
                            variant={isPreviewMode ? "secondary" : "outline"}
                            size="sm"
                            className="flex items-center gap-1.5"
                            onClick={handlePreviewClick}
                        >
                            {isPreviewMode ? <Eye className="h-3.5 w-3.5" /> : <EyeClosed className="h-3.5 w-3.5" />}
                            {isPreviewMode ? t("admin.lms.editView") : t("common.actions.preview")}
                        </Button>
                        <Button onClick={handleTogglePublish} size="sm">
                            {course.status === "PUBLISHED"
                                ? t("common.actions.unpublish")
                                : t("common.actions.publish")}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-1 overflow-hidden">
                    <CourseOutline
                        sidebarCollapsed={sidebarCollapsed}
                        setSidebarCollapsed={setSidebarCollapsed}
                        modules={modules}
                        highlightSection={highlightSection}
                        onToggleVisibility={toggleContentStatus}
                    />
                    {isPreviewMode ? (
                        <LearnerPreview
                            modules={modules}
                            unmoduledContent={unmoduledContent}
                            handlePreviewClicK={handlePreviewClick}
                        />
                    ) : (
                        <div className="bg-muted/30 flex-1 overflow-y-auto p-8">
                            <div className="flex items-start gap-6">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-row items-center gap-2 py-2">
                                        <StatusBadge
                                            dot
                                            className="bg-card/95"
                                            variant={course.status === "PUBLISHED" ? "success" : "warning"}
                                            textClassName="text-primary"
                                        >
                                            {course.status === "PUBLISHED" ? "Published" : "Draft"}
                                        </StatusBadge>
                                        {course.shared ? (
                                            <Badge variant="secondary">{t("admin.lms.sharedCourse")}</Badge>
                                        ) : null}
                                        {isGlobalAdmin ? (
                                            <OrganizationSelect
                                                value={course.organizationId}
                                                onValueChange={changeOrganization}
                                                className="h-7 w-56"
                                                aria-label="Organization"
                                            />
                                        ) : course.organization?.name ? (
                                            <Badge variant="outline">{course.organization.name}</Badge>
                                        ) : null}
                                        <label className="text-muted-foreground flex items-center gap-2 text-sm">
                                            <Switch
                                                checked={course.assignedOnly}
                                                onCheckedChange={changeAssignedOnly}
                                            />
                                            {t("admin.lms.assignedOnly")}
                                        </label>
                                    </div>
                                    <div className="group/title relative">
                                        <input
                                            value={title}
                                            onChange={(e) => {
                                                setTitle(e.target.value);
                                                scheduleSave(e.target.value, description);
                                            }}
                                            className="font-display text-text-heading hover:border-border focus:border-secondary focus:bg-card w-full rounded-lg border border-transparent bg-transparent p-1.5 text-3xl font-bold tracking-tight transition-colors outline-none"
                                        />
                                        <Pencil className="text-muted-foreground/40 pointer-events-none absolute top-1/2 right-2 h-3.5 w-3.5 -translate-y-1/2" />
                                    </div>
                                    <div className="group/desc relative pt-0.5">
                                        <textarea
                                            value={description}
                                            rows={2}
                                            onChange={(e) => {
                                                setDescription(e.target.value);
                                                scheduleSave(title, e.target.value);
                                            }}
                                            placeholder={"Enter a course description"}
                                            className="text-text-body hover:border-border focus:border-secondary focus:bg-card w-full resize-none rounded-lg border border-transparent bg-transparent p-1.5 font-sans text-base transition-colors outline-none"
                                        />
                                        <Pencil className="text-muted-foreground/40 pointer-events-none absolute top-2 right-2 h-3.5 w-3.5" />
                                    </div>
                                    <Mono size="xs" className="pl-2">
                                        {modules.length} {t("admin.lms.modules")} · {allContent.length}{" "}
                                        {t("admin.lms.items")}
                                    </Mono>
                                </div>
                            </div>
                            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
                                {/* Left column */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-row items-center justify-between">
                                        <Heading level={4}>{t("admin.lms.curriculum")}</Heading>
                                        <div className="flex flex-row items-center gap-2">
                                            <Pencil size={"0.8em"} className="text-text-eyebrow" />
                                            <Mono size="xs">{t("admin.lms.clickFieldToEdit")} · </Mono>
                                            <GripVertical size={"0.9em"} className="text-text-eyebrow" />{" "}
                                            <Mono size="xs">{t("admin.lms.dragToReorder")}</Mono>
                                        </div>
                                    </div>
                                    <DndContext
                                        sensors={moduleSensors}
                                        collisionDetection={closestCenter}
                                        onDragStart={handleModuleDragStart}
                                        onDragEnd={handleModuleDragEnd}
                                    >
                                        <SortableContext
                                            items={modules.map((m) => m.id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {modules.map((module, idx) => (
                                                <SortableModuleWrapper key={module.id} module={module}>
                                                    {({ listeners, attributes, setNodeRef, isDragging }) => (
                                                        <ModuleCard
                                                            id={module.id}
                                                            index={idx + 1}
                                                            title={module.title}
                                                            itemCount={module.content.length}
                                                            contentIds={module.content.map((c) => c.id)}
                                                            renderContent={(contentId, dragProps) => {
                                                                const content = module.content.find(
                                                                    (c) => c.id === contentId,
                                                                );
                                                                if (!content) return null;
                                                                return (
                                                                    <ContentRow
                                                                        key={content.id}
                                                                        {...content}
                                                                        isActive={activeSection === content.id}
                                                                        onEdit={() => setEditingContentId(content.id)}
                                                                        onToggleVisibility={() =>
                                                                            toggleContentStatus(content.id)
                                                                        }
                                                                        onClick={() => setEditingContentId(content.id)}
                                                                        setNodeRef={dragProps.setNodeRef}
                                                                        listeners={dragProps.listeners}
                                                                        attributes={dragProps.attributes}
                                                                        isDragging={dragProps.isDragging}
                                                                        onHandleDelete={() =>
                                                                            deleteContentItem(content.id)
                                                                        }
                                                                    />
                                                                );
                                                            }}
                                                            onContentReorder={(from, to) =>
                                                                handleContentReorder(module.id, from, to)
                                                            }
                                                            onAddContent={() => setAddingContentToModule(module.id)}
                                                            addingContent={addingContentToModule === module.id}
                                                            onPickType={(type) => addContentToModule(module.id, type)}
                                                            onCancelAdd={() => setAddingContentToModule(null)}
                                                            onTitleChange={(newTitle) => {
                                                                setModules((prev) =>
                                                                    prev.map((m) =>
                                                                        m.id === module.id
                                                                            ? { ...m, title: newTitle }
                                                                            : m,
                                                                    ),
                                                                );
                                                                if (saveTimeout.current)
                                                                    clearTimeout(saveTimeout.current);
                                                                saveTimeout.current = setTimeout(() => {
                                                                    save(() =>
                                                                        updateModule({
                                                                            variables: {
                                                                                id: module.id,
                                                                                input: { title: newTitle },
                                                                            },
                                                                        }),
                                                                    );
                                                                }, 800);
                                                            }}
                                                            onDelete={() => deleteModuleItem(module.id)}
                                                            listeners={listeners}
                                                            attributes={attributes}
                                                            setNodeRef={setNodeRef}
                                                            isDragging={isDragging}
                                                        />
                                                    )}
                                                </SortableModuleWrapper>
                                            ))}
                                        </SortableContext>
                                        <DragOverlay>
                                            {activeModuleId
                                                ? (() => {
                                                      const activeModule = modules.find((m) => m.id === activeModuleId);
                                                      const activeIdx = modules.findIndex(
                                                          (m) => m.id === activeModuleId,
                                                      );
                                                      if (!activeModule) return null;
                                                      return (
                                                          <ModuleCard
                                                              id={activeModule.id}
                                                              index={activeIdx + 1}
                                                              title={activeModule.title}
                                                              itemCount={activeModule.content.length}
                                                              contentIds={activeModule.content.map((c) => c.id)}
                                                              renderContent={(contentId) => {
                                                                  const content = activeModule.content.find(
                                                                      (c) => c.id === contentId,
                                                                  );
                                                                  if (!content) return null;
                                                                  return (
                                                                      <ContentRow
                                                                          key={content.id}
                                                                          {...content}
                                                                          isActive={false}
                                                                          onEdit={() => {}}
                                                                          onToggleVisibility={() => {}}
                                                                          onHandleDelete={() => {}}
                                                                      />
                                                                  );
                                                              }}
                                                              className="rotate-1 opacity-95 shadow-2xl"
                                                          />
                                                      );
                                                  })()
                                                : null}
                                        </DragOverlay>
                                    </DndContext>
                                    {unmoduledContent.length > 0 && (
                                        <Card className="w-full max-w-none overflow-hidden">
                                            <div className="bg-background flex items-center justify-between border-b px-4 py-3">
                                                <Text weight="semibold" size="sm">
                                                    {t("admin.lms.ungroupedItems")}
                                                </Text>
                                                <Mono size="xs" muted>
                                                    {unmoduledContent.length}{" "}
                                                    {unmoduledContent.length === 1 ? "item" : "items"}
                                                </Mono>
                                            </div>
                                            <div className="flex flex-col">
                                                {unmoduledContent.map((content) => (
                                                    <ContentRow
                                                        key={content.id}
                                                        {...content}
                                                        isActive={activeSection === content.id}
                                                        onEdit={() => setEditingContentId(content.id)}
                                                        onToggleVisibility={() => toggleContentStatus(content.id)}
                                                        onClick={() => setEditingContentId(content.id)}
                                                        onHandleDelete={() => deleteContentItem(content.id)}
                                                    />
                                                ))}
                                            </div>
                                        </Card>
                                    )}
                                    <button
                                        onClick={addModule}
                                        className="border-border text-muted-foreground hover:text-foreground flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-4 text-sm transition-colors"
                                    >
                                        <Plus className="h-4 w-4" />
                                        {t("admin.lms.addModule")}
                                    </button>
                                </div>
                                {/* Right column */}
                                <div className="flex flex-col gap-4">
                                    <CourseDetailsBuilder
                                        course={course}
                                        contentLength={allContent.length}
                                        numModules={modules.length}
                                        author={author}
                                    />
                                    <CoursePresentersTable
                                        presenters={presenters}
                                        presentersLoading={presentersLoading}
                                        presentersError={presentersError}
                                        selectedPresenterIds={selectedPresenters}
                                        togglePresenter={togglePresenter}
                                    />
                                    <CourseTagsTable
                                        tags={tags}
                                        inheritedTags={(course?.allTags ?? []).filter(
                                            (tag) => !selectedTags.has(tag.id),
                                        )}
                                        tagsLoading={tagsLoading}
                                        tagsError={tagsError}
                                        selectedTags={selectedTags}
                                        toggleTag={toggleTag}
                                        tagsModalOpen={tagsOpen}
                                        setTagsModalOpen={setTagsOpen}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ContentEditor
                editingContentId={editingContentId}
                setEditingContentId={setEditingContentId}
                allContent={allContent}
                organizationId={course.organizationId}
                onSave={save}
            />
        </div>
    );
};

export default CoursePreview;
